import { Immutable } from "immer";
import React, { useEffect, useRef } from "react";
import { t } from "ttag";

import type { SharedFeaturedProductLinesBlock as Value } from "@reactivated";

import { constants } from "@reactivated/constants";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import {
    IGA4ListItemPayload,
    IGA4ListMetadata,
    trackSelectItemEventWithItem,
    trackViewItemListEventWithItems,
} from "../../utils/analytics";
import { money } from "../../utils/format";
import { usePageSetting, useShouldShowPricing } from "../../utils/settings";
import { hasRichTextContent } from "../../utils/streamfieldContent";
import { slugify } from "../../utils/text";
import { isElementVisible } from "../../utils/visibility";
import { Flair } from "../Flair";
import { RatingGraphic } from "../RatingGraphic";
import { TSIAlignedRichTextBlock } from "../TSIAlignedRichTextBlock";
import { getGlobalSpacingClasses } from "../_spacing/globalSpacing";

const AppSlug = constants["tsicommon.types.AppSlug"];

type IProps = Immutable<{
    value: Value;
}>;

type ProductLine = NonNullable<Value["product_lines"]>[number];
type ProductPrice = NonNullable<ProductLine["product_price"]>;

/**
 * Faithful client port of the legacy server-rendered
 * ``tsicommon_cms/blocks/featured-product-lines-block.html``.
 *
 * The serialized value carries only ``product`` (a pk) and ``category``
 * (``{id, name}``) per line; the legacy template computed pricing and category
 * review aggregates SERVER-side. Those move client-side here:
 *
 *   - Pricing: serialized SERVER-side into ``line.product_price`` by the block
 *     (``purchase_info_for_product`` -> ``fetch_for_parent`` for the parent
 *     product, mirroring the legacy tag). The block's products are PARENT
 *     products, and the product-detail API the client would otherwise fetch
 *     resolves a parent via ``fetch_for_product`` -> an Unavailable (null)
 *     price, so the "from" price MUST come from the server. We hand-render the
 *     legacy ``product-line-card__price--actual/--strikethrough/--highlight``
 *     GLOBAL spans (DynamicPrice's CSS-module classes don't match the block's
 *     load-bearing global SCSS), reusing the ``useShouldShowPricing`` gate,
 *     ``money()`` formatter, and branch condition
 *     (``cosmetic_incl_tax !== incl_tax``).
 *
 *   - Rating: SSR'd. The block serializes ``Category.review_info`` (summed
 *     ``num_reviews`` + averaged ``rating``) into ``line.category_review_info``
 *     server-side, so the stars render on the server with no ``loadCategory``
 *     fetch on mount. Rendered with ``RatingGraphic`` (whose half-star /
 *     gradient markup the block SCSS was written for; it self-hides on tempurca
 *     and emits the load-bearing "{N} Reviews" text).
 *
 *   - hide_rating: the legacy ``<article>`` class on
 *     ``num_reviews == 0 OR tempurca`` (RatingGraphic's internal tempurca hide
 *     only covers the stars, not the article class or the zero-reviews case).
 *
 * Note: the legacy ``value.top_flair`` referenced a field that does not exist on
 * the block, so the flair always rendered in the ``not top_flair`` branch
 * (below the image); reproduced here. The legacy image ``alt`` resolved to the
 * (unbound) page ``product`` and was effectively the image title, so we use the
 * image title.
 *
 * The ``view_item_list`` impression is also SSR'd: the block serializes the GA4
 * item payload server-side into ``view_item_list_items`` (built by the same
 * ``build_catalog_item`` helper the legacy ``track_view_item_list`` tag used,
 * over each line's ``.product`` FK -- NOT the category review-info product
 * sets), so the impression fires once on mount from server data with NO client
 * fetch. The client supplies the list metadata (see ``buildListMetadata`` for
 * the item_list_id/item_list_name parity with the server tag).
 */

/**
 * Hand-render the legacy ``product-line-card__price`` markup (global classes the
 * block SCSS targets), reusing DynamicPrice's discount branch condition
 * (``cosmetic_incl_tax !== incl_tax``) and ``money()`` formatter. ``<small>``
 * renders in both branches, matching the legacy template.
 */
const renderPrice = (price: Immutable<ProductPrice>): React.JSX.Element => {
    const isDiscounted =
        !!price.cosmetic_incl_tax && price.cosmetic_incl_tax !== price.incl_tax;
    return (
        <div className="product-line-card__price">
            <small>{t`Queens starting at`}</small>
            {!isDiscounted ? (
                <span className="product-line-card__price--actual">
                    {money(price.incl_tax)}
                </span>
            ) : (
                <>
                    <span className="product-line-card__price--retail">
                        <span className="ada-screenreader-only">
                            {t`Original Price:`}
                        </span>
                        <span className="product-line-card__price--strikethrough">
                            {money(price.incl_tax)}
                        </span>
                    </span>
                    <span className="ada-screenreader-only">
                        {t`Current Price:`}
                    </span>
                    <span className="product-line-card__price--highlight">
                        {money(price.cosmetic_incl_tax)}
                    </span>
                </>
            )}
        </div>
    );
};

type ProductLineCardProps = Immutable<{
    line: ProductLine;
    index: number;
    listMetadata: IGA4ListMetadata;
    viewItemListItems: readonly IGA4ListItemPayload[];
}>;

const ProductLineCard = (props: ProductLineCardProps): React.JSX.Element => {
    const line = props.line;
    const shouldShowPricing = useShouldShowPricing();
    const appSlug = usePageSetting("app_slug") || "";

    // The category's rolled-up review aggregate (``Category.review_info``) is
    // SSR'd into ``line.category_review_info``, so the stars render server-side
    // with no ``loadCategory`` fetch on mount.
    const reviewInfo = line.category_review_info;
    const numReviews = reviewInfo?.num_reviews ?? 0;
    const hideRating = numReviews === 0 || appSlug === AppSlug.TEMPURCA;

    const cardClasses = concatClassNames([
        "product-line-card",
        hideRating ? "hide_rating" : "",
    ]);

    const image = line.product_image;
    const imageSrc = image?.renditions["width-1000"]?.url ?? image?.url;

    // The block serializes the parent "from"-price server-side, and only when
    // the price exists -- so its presence is the client equivalent of the
    // legacy `purchase_info.price.exists` gate, and `useShouldShowPricing`
    // mirrors `site_settings.should_show_pricing`.
    const price = line.product_price;
    const showPrice = shouldShowPricing && price ? price : null;

    const handleFooterContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target instanceof HTMLElement && e.target.closest("a")) {
            // Fire select_item when a link inside footer_content is clicked,
            // using the server-serialized item at this card's index in
            // view_item_list_items (same order as product_lines).
            const item = props.viewItemListItems[props.index];
            if (item) {
                trackSelectItemEventWithItem(item, props.listMetadata);
            }
        }
    };

    return (
        <article
            role="group"
            aria-label={t`product line`}
            className={cardClasses}
        >
            {imageSrc && (
                <img
                    src={imageSrc}
                    alt={image?.title ?? ""}
                    className=""
                    loading="lazy"
                    decoding="async"
                />
            )}

            {line.flair?.content && (
                <Flair
                    flair={{
                        style: line.flair.style,
                        content: line.flair.content,
                    }}
                />
            )}

            {line.header_content && (
                <RichTextBlock value={line.header_content} />
            )}

            {reviewInfo && (
                <RatingGraphic
                    rating={reviewInfo.rating}
                    numReviews={reviewInfo.num_reviews}
                    onlyReviewsNum={false}
                    cardClass={"star-rating"}
                    cardSize={"small"}
                />
            )}

            {line.description_content && (
                <RichTextBlock value={line.description_content} />
            )}

            {showPrice && renderPrice(showPrice)}

            {line.footer_content && (
                <div onClick={handleFooterContentClick}>
                    <RichTextBlock value={line.footer_content} />
                </div>
            )}
        </article>
    );
};

// Literal GA4 list-name fallback, matching the legacy Django tag's
// ``fallback_name='Featured Product Lines'`` (the block has no editor heading
// field that the server tag's name-resolution consumes, so it always fell back
// to this literal).
const FALLBACK_LIST_NAME = "Featured Product Lines";

/**
 * Reproduce the server ``_build_item_list_metadata`` output for this block.
 *
 * The server ``_normalize_tracking_name_id`` falls back to ``fallback_name``
 * ('Featured Product Lines') for ``item_list_name`` and ``slugify(fallback_name)``
 * for the base id. The Django template passed ``block_index=self.id``; ``self``
 * is the StructValue, which has no ``id`` child, so ``self.id`` resolved to
 * ``''`` (Django ``string_if_invalid``). Since ``'' is not None``, the server
 * appended ``-{block_index}`` and produced a TRAILING-HYPHEN id
 * (``featured-product-lines-``). We reproduce that exactly so the client
 * impression lands in the same GA4 list the server historically emitted.
 */
const buildListMetadata = (
    currency: string | null | undefined,
): IGA4ListMetadata => ({
    item_list_id: `${slugify(FALLBACK_LIST_NAME)}-`,
    item_list_name: FALLBACK_LIST_NAME,
    ...(currency ? { currency } : {}),
});

export const FeaturedProductLinesBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const lines = v.product_lines ?? [];

    const firedRef = useRef(false);
    const sectionRef = useRef<HTMLElement>(null);

    // Pull currency from the first line's server-serialized product_price so
    // GA4 events on non-USD sites (e.g. TEMPURCA / CAD) don't fall back to the
    // module-level USD default in ``trackViewItemListEventWithItems`` /
    // ``trackSelectItemEventWithItem``.
    const listMetadata = buildListMetadata(lines[0]?.product_price?.currency);

    // Fire the ``view_item_list`` impression exactly once on mount from the
    // server-serialized GA4 items (``view_item_list_items``, built by the same
    // ``build_catalog_item`` helper as the legacy ``track_view_item_list``
    // tag). No client product fetch: every on-load value is SSR'd. The
    // ``firedRef`` guard prevents a re-fire if a later render re-runs this
    // effect. The visibility check skips hidden responsive variants (e.g. a
    // desktop-only block that is CSS-hidden on mobile).
    useEffect(() => {
        if (firedRef.current) {
            return;
        }
        firedRef.current = true;
        if (!isElementVisible(sectionRef.current)) {
            return;
        }
        trackViewItemListEventWithItems(v.view_item_list_items, listMetadata);
        // Fire-on-mount only: the block value is static per page render.
    }, []);

    return (
        <section
            ref={sectionRef}
            className={concatClassNames([
                "featured-product-lines-block",
                v.background_style?.style ?? "",
                v.responsive_visibility?.visibility ?? "",
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            {/* Legacy gate: `{% if value.header_content.content.source %}`.
                An unset rich text serializes to a truthy
                `<div class="rich-text"></div>`, so gate on REAL content. */}
            {v.header_content &&
                hasRichTextContent(v.header_content.content) && (
                    <TSIAlignedRichTextBlock value={v.header_content} />
                )}

            <div>
                {lines.map((line, i) => (
                    <ProductLineCard
                        key={i}
                        line={line}
                        index={i}
                        listMetadata={listMetadata}
                        viewItemListItems={v.view_item_list_items}
                    />
                ))}
            </div>
        </section>
    );
};
